import type { RemunerationIndemnityPayload } from "../schemas/remuneration/remuneration-profile-indemnity";
import { normalizeComplementLabel } from "../pld/labeled-prime-ids";
import {
  remunerationProfileInsertPayloadSchema,
  remunerationProfilePayTrackingReadSchema,
  type RemunerationProfileInsertPayload,
  type RemunerationProfilePayTrackingReadPayload,
  type RemunerationProfileUpdatePayload,
} from "../schemas/remuneration/remuneration-profile";
import type { RemunerationProfileRow } from "./supabase.types";
import type { GetRemunerationProfileResponse } from "../schemas/remuneration/remuneration-profile";
import {
  REMUNERATION_PROFILE_ORIGIN,
  type FrenchRemunerationProfileData,
  frenchRemunerationProfileDataSchema,
  resolveRemunerationProfileOrigin,
} from "../schemas/remuneration/remuneration-profile-data";
import { SMIC_MINIMUM } from "./remuneration.constants";
import {
  computeThirteenthMonthPrime,
  nextThirteenthMonthAmountAfterProfileChange,
  normalizeThirteenthMonthUnit,
  resolveThirteenthMonthAmount,
} from "./remuneration";
import type { EmployeeStatus } from "./supabase.types/enums";

export type LegacyContractRemunerationSource = {
  hourly_wage: number;
  coefficient: number;
  employee_status?: EmployeeStatus | null;
};

const mapIndemnityPayloadsToStored = (
  indemnities: RemunerationIndemnityPayload[]
): FrenchRemunerationProfileData["indemnities"] =>
  indemnities.map((indemnity) => ({
    id: crypto.randomUUID(),
    pld_prime_id: indemnity.pld_prime_id,
    pld_prime_id_taxable: indemnity.pld_prime_id_taxable,
    name: indemnity.name,
    complement_label: normalizeComplementLabel(indemnity.complement_label),
    amount: indemnity.amount,
    amount_non_taxable: indemnity.amount_non_taxable,
    // Non-taxable prime coefficient is always 1 (business rule).
    coefficient: 1,
    taxable_coefficient: indemnity.taxable_coefficient,
  }));

export const buildFrenchProfileDataFromLegacyContractFields = (
  legacyContractFields: LegacyContractRemunerationSource
): FrenchRemunerationProfileData =>
  frenchRemunerationProfileDataSchema.parse({
    profile_origin: REMUNERATION_PROFILE_ORIGIN.LEGACY_IMPORT,
    // Legacy contracts may carry coefficient 0 before structured remu; profile schema requires >= 1.
    hours_coefficient: Math.max(legacyContractFields.coefficient, 1),
    bonus_coefficient: 1,
    employee_status: legacyContractFields.employee_status ?? null,
    hourly_rate: legacyContractFields.hourly_wage,
    thirteenth_month_enabled: false,
    thirteenth_month_unit: "hour",
    thirteenth_month_amount: null,
    rtt_enabled: false,
    rtt_weekly_hours: null,
    overtime_enabled: false,
    overtime_has_custom_convention: null,
    overtime_rate_36_to_43_percent: null,
    overtime_rate_above_43_percent: null,
    indemnities: [],
  });

const resolveStoredThirteenthMonthAmount = (
  profile: Pick<
    FrenchRemunerationProfileData,
    "thirteenth_month_enabled" | "thirteenth_month_unit" | "thirteenth_month_amount" | "hourly_rate"
  >
): number | null =>
  resolveThirteenthMonthAmount({
    hourlyRate: profile.hourly_rate,
    thirteenthMonthEnabled: profile.thirteenth_month_enabled,
    thirteenthMonthUnit: profile.thirteenth_month_unit,
    thirteenthMonthAmount: profile.thirteenth_month_amount,
  }) ?? null;

export const buildFrenchProfileDataFromInsertPayload = (
  payload: RemunerationProfileInsertPayload
): FrenchRemunerationProfileData => {
  const { indemnities, ...scalars } = payload;
  const thirteenthMonthUnit = normalizeThirteenthMonthUnit(payload.thirteenth_month_unit);

  return frenchRemunerationProfileDataSchema.parse({
    ...scalars,
    profile_origin: "structured",
    thirteenth_month_unit: thirteenthMonthUnit,
    thirteenth_month_amount: payload.thirteenth_month_enabled
      ? (payload.thirteenth_month_amount ?? computeThirteenthMonthPrime(payload.hourly_rate, thirteenthMonthUnit))
      : null,
    rtt_weekly_hours: payload.rtt_weekly_hours ?? null,
    overtime_has_custom_convention: payload.overtime_has_custom_convention ?? null,
    overtime_rate_36_to_43_percent: payload.overtime_rate_36_to_43_percent ?? null,
    overtime_rate_above_43_percent: payload.overtime_rate_above_43_percent ?? null,
    indemnities: mapIndemnityPayloadsToStored(indemnities),
  });
};

export const mergeFrenchProfileDataForUpdate = (
  existingProfile: FrenchRemunerationProfileData,
  patch: RemunerationProfileUpdatePayload
): FrenchRemunerationProfileData => {
  const { indemnities, ...scalarPatch } = patch;
  const merged: FrenchRemunerationProfileData = {
    ...existingProfile,
    ...scalarPatch,
  };

  if (indemnities !== undefined) {
    merged.indemnities = mapIndemnityPayloadsToStored(indemnities);
  }

  merged.thirteenth_month_unit = normalizeThirteenthMonthUnit(merged.thirteenth_month_unit);
  merged.thirteenth_month_amount = resolveStoredThirteenthMonthAmount({
    hourly_rate: merged.hourly_rate,
    thirteenth_month_enabled: merged.thirteenth_month_enabled,
    thirteenth_month_unit: merged.thirteenth_month_unit,
    thirteenth_month_amount: nextThirteenthMonthAmountAfterProfileChange({
      previousHourlyRate: existingProfile.hourly_rate,
      previousThirteenthMonthUnit: normalizeThirteenthMonthUnit(existingProfile.thirteenth_month_unit),
      previousThirteenthMonthEnabled: existingProfile.thirteenth_month_enabled,
      previousThirteenthMonthAmount: existingProfile.thirteenth_month_amount ?? undefined,
      patch: scalarPatch,
    }),
  });

  return frenchRemunerationProfileDataSchema.parse(merged);
};

export const parseFrenchProfileData = (profile: unknown): FrenchRemunerationProfileData =>
  frenchRemunerationProfileDataSchema.parse(profile);

export const mapFrenchProfileDataToPayTrackingReadPayload = (
  profile: FrenchRemunerationProfileData
): RemunerationProfilePayTrackingReadPayload =>
  remunerationProfilePayTrackingReadSchema.parse({
    hours_coefficient: profile.hours_coefficient,
    bonus_coefficient: profile.bonus_coefficient,
    employee_status: profile.employee_status,
    hourly_rate: profile.hourly_rate,
    thirteenth_month_enabled: profile.thirteenth_month_enabled,
    thirteenth_month_unit: normalizeThirteenthMonthUnit(profile.thirteenth_month_unit),
    thirteenth_month_amount: resolveThirteenthMonthAmount({
      hourlyRate: profile.hourly_rate,
      thirteenthMonthEnabled: profile.thirteenth_month_enabled,
      thirteenthMonthUnit: profile.thirteenth_month_unit,
      thirteenthMonthAmount: profile.thirteenth_month_amount,
    }),
    rtt_enabled: profile.rtt_enabled,
    rtt_weekly_hours: profile.rtt_weekly_hours ?? undefined,
    overtime_enabled: profile.overtime_enabled,
    overtime_has_custom_convention: profile.overtime_enabled
      ? (profile.overtime_has_custom_convention ?? false)
      : undefined,
    overtime_rate_36_to_43_percent: profile.overtime_rate_36_to_43_percent ?? undefined,
    overtime_rate_above_43_percent: profile.overtime_rate_above_43_percent ?? undefined,
    indemnities: profile.indemnities.map(({ id: _id, ...indemnity }) => indemnity),
  });

export const mapFrenchProfileDataToInsertPayload = (
  profile: FrenchRemunerationProfileData
): RemunerationProfileInsertPayload =>
  remunerationProfileInsertPayloadSchema.parse({
    hours_coefficient: profile.hours_coefficient,
    bonus_coefficient: profile.bonus_coefficient,
    employee_status: profile.employee_status,
    hourly_rate: profile.hourly_rate,
    thirteenth_month_enabled: profile.thirteenth_month_enabled,
    thirteenth_month_unit: normalizeThirteenthMonthUnit(profile.thirteenth_month_unit),
    thirteenth_month_amount: resolveThirteenthMonthAmount({
      hourlyRate: profile.hourly_rate,
      thirteenthMonthEnabled: profile.thirteenth_month_enabled,
      thirteenthMonthUnit: profile.thirteenth_month_unit,
      thirteenthMonthAmount: profile.thirteenth_month_amount,
    }),
    rtt_enabled: profile.rtt_enabled,
    rtt_weekly_hours: profile.rtt_weekly_hours ?? undefined,
    overtime_enabled: profile.overtime_enabled,
    overtime_has_custom_convention: profile.overtime_enabled
      ? (profile.overtime_has_custom_convention ?? false)
      : undefined,
    overtime_rate_36_to_43_percent: profile.overtime_rate_36_to_43_percent ?? undefined,
    overtime_rate_above_43_percent: profile.overtime_rate_above_43_percent ?? undefined,
    indemnities: profile.indemnities.map(({ id: _id, ...indemnity }) => indemnity),
  });

export const mapRemunerationProfileRowToInsertPayload = (
  row: RemunerationProfileRow
): RemunerationProfileInsertPayload => mapFrenchProfileDataToInsertPayload(row.profile);

export const mapRemunerationProfileRowToGetResponse = (
  row: RemunerationProfileRow
): GetRemunerationProfileResponse => ({
  ...mapRemunerationProfileRowToInsertPayload(row),
  profile_origin: resolveRemunerationProfileOrigin(row.profile),
});

export const getDefaultFrenchRemunerationProfileData = (): FrenchRemunerationProfileData =>
  frenchRemunerationProfileDataSchema.parse({
    profile_origin: "structured",
    hours_coefficient: 1.2,
    bonus_coefficient: 1,
    employee_status: null,
    hourly_rate: 12,
    thirteenth_month_enabled: false,
    thirteenth_month_unit: "hour",
    thirteenth_month_amount: null,
    rtt_enabled: false,
    rtt_weekly_hours: null,
    overtime_enabled: false,
    overtime_has_custom_convention: null,
    overtime_rate_36_to_43_percent: null,
    overtime_rate_above_43_percent: null,
    indemnities: [],
  });

/** Insert payload defaults for forms: shared profile defaults with SMIC as minimum hourly rate. */
export const getDefaultRemunerationProfileInsertPayload = (): RemunerationProfileInsertPayload => {
  const defaultProfile = getDefaultFrenchRemunerationProfileData();

  return {
    hours_coefficient: defaultProfile.hours_coefficient,
    bonus_coefficient: defaultProfile.bonus_coefficient,
    employee_status: defaultProfile.employee_status,
    hourly_rate: SMIC_MINIMUM,
    thirteenth_month_enabled: defaultProfile.thirteenth_month_enabled,
    thirteenth_month_unit: defaultProfile.thirteenth_month_unit ?? "hour",
    rtt_enabled: defaultProfile.rtt_enabled,
    rtt_weekly_hours: defaultProfile.rtt_weekly_hours ?? undefined,
    overtime_enabled: defaultProfile.overtime_enabled,
    overtime_has_custom_convention: defaultProfile.overtime_has_custom_convention ?? undefined,
    overtime_rate_36_to_43_percent: defaultProfile.overtime_rate_36_to_43_percent ?? undefined,
    overtime_rate_above_43_percent: defaultProfile.overtime_rate_above_43_percent ?? undefined,
    indemnities: [],
  };
};
